import { Component, OnDestroy, OnInit } from '@angular/core';
import { Chart } from 'chart.js';
import * as moment from 'moment';
import { Subscription, interval, takeUntil, takeWhile } from 'rxjs';
import { DashboardCard } from 'src/app/core/models/dashboard.card.model';
import { DashboardCardResponse } from 'src/app/core/models/response/dashboard-cards.model';
import { DashboardLocalizacaoResponse } from 'src/app/core/models/response/dashboard-localizacao.model';
import { DashboardMovimentoResponse } from 'src/app/core/models/response/dashboard-movimento.model';
import { WeblabService } from 'src/app/core/services/weblab.service';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss']
})
export class DashboardComponent implements OnInit, OnDestroy {
  cards: Array<DashboardCard>;
  basicData: any;
  basicOptions: any;
  data: any;
  options: any;
  // interval$: Subscription;
  intervallTimer = interval(1000 * 60); //1 minuto
  refreshAtivo = true;
  refreshIniciado: boolean = false;

  responsiveOptions: any[] | undefined;

  busyCards: boolean = false;
  busyLocalizacoes: boolean = false;
  busyMovimentacoes: boolean = false;

  // chartHeight: number;

  lblLocalizacoes: string[] = [];
  vlrLocalizacoes: number[] = [];

  lblMovimentacoes: string[] = [];
  vlrMovimentacoesValidacao: number[] = [];
  vlrMovimentacoesProducao: number[] = [];
  vlrMovimentacoesAprovados: number[] = [];
  vlrMovimentacoesEntregues: number[] = [];
  vlrMovimentacoesCancelados: number[] = [];

  constructor(private service: WeblabService) { }

  ngOnInit(): void {
    // this.chartHeight = window.innerHeight / 2;

    this.carregaDashboard();

    this.responsiveOptions = [
      {
        breakpoint: '1199px',
        numVisible: 3,
        numScroll: 1
      },
      {
        breakpoint: '991px',
        numVisible: 3,
        numScroll: 1
      },
      {
        breakpoint: '767px',
        numVisible: 2,
        numScroll: 1
      }
    ];

    setTimeout(() => {
      this.refreshIniciado = true;
      this.iniciandoRefresh();
    }, 1000);
  }

  ngOnDestroy(): void {
    this.refreshAtivo = false;
  }

  iniciandoRefresh() {

    this.intervallTimer.pipe(
      takeWhile(() => this.refreshAtivo)
    ).subscribe(() => {
      this.carregaDashboard();
    });
  }

  carregaDashboard() {
    this.buscaCards();
  }

  private buscaCards() {
    //Cards
    this.busyCards = !this.refreshIniciado ? true : false;
    const cards$ = this.service.dashboardCards();
    cards$.subscribe({
      next: (resp: DashboardCardResponse) => this.cardSucesso(resp),
      error: (error: any) => this.cardErro(error)
    });
  }

  private cardSucesso(cards: DashboardCardResponse) {
    this.carregaCards(cards);
    this.busyCards = false;
    this.buscaLocalizacoes();
  }

  private cardErro(error: any) {
    this.busyCards = false;
    this.buscaLocalizacoes();
  }

  private buscaLocalizacoes() {
    // Grafico 1
    this.busyLocalizacoes = !this.refreshIniciado ? true : false;
    const localizacoes$ = this.service.dashboardLocalizacoes();
    localizacoes$.subscribe({
      next: (resp: DashboardLocalizacaoResponse) => this.localizacoesSucesso(resp),
      error: (error: any) => this.localizacoesErro(error)
    });
  }

  private localizacoesSucesso(localizacoes: DashboardLocalizacaoResponse) {
    this.lblLocalizacoes = [];
    this.vlrLocalizacoes = [];

    localizacoes.locations?.sort((a, b) => {
      const nameA = a.status.replace(/\s/g, "").toUpperCase();
      const nameB = b.status.replace(/\s/g, "").toUpperCase();
      if (nameA < nameB) {
        return -1;
      }
      if (nameA > nameB) {
        return 1;
      }

      // names must be equal
      return 0;
    });

    localizacoes.locations.forEach(localizacao => {
      this.lblLocalizacoes.push(localizacao.status);
      this.vlrLocalizacoes.push(localizacao.quantity);
    });

    this.carregaGraficoLocalizacoes();
    this.busyLocalizacoes = false;
    this.buscaMovimentacoes();
  }

  private localizacoesErro(error: any) {
    this.busyLocalizacoes = false;
    this.buscaMovimentacoes();
  }

  private buscaMovimentacoes() {
    // Grafico 2
    this.busyMovimentacoes = !this.refreshIniciado ? true : false;

    let periodoInicial = moment().add(-12, 'month').startOf('month').format("DD/MM/yyyy");
    let periodoFinal = moment().format("DD/MM/yyyy");

    const movimentacoes$ = this.service.dashboardMovimentacoes(periodoInicial, periodoFinal);
    movimentacoes$.subscribe({
      next: (resp: DashboardMovimentoResponse) => this.movimentacoesSucesso(resp),
      error: (error: any) => this.movimentacoesErro(error)
    });
  }

  private movimentacoesSucesso(movimentacoes: DashboardMovimentoResponse) {
    this.lblMovimentacoes = [];

    this.vlrMovimentacoesValidacao = [];
    this.vlrMovimentacoesProducao = [];
    this.vlrMovimentacoesAprovados = [];
    this.vlrMovimentacoesEntregues = [];
    this.vlrMovimentacoesCancelados = [];

    const movimentos = movimentacoes.Movements.sort(function (a, b) {
      return (a.ano + a.mes) - (b.ano + b.mes);
    });

    movimentos.forEach(movimentacao => {
      this.lblMovimentacoes.push(`${movimentacao.mes}/${movimentacao.ano}`);

      this.vlrMovimentacoesValidacao.push(movimentacao.validacao);
      this.vlrMovimentacoesProducao.push(movimentacao.producao);
      this.vlrMovimentacoesAprovados.push(movimentacao.aprovado);
      this.vlrMovimentacoesEntregues.push(movimentacao.entregue);
      this.vlrMovimentacoesCancelados.push(movimentacao.cancelado);
    });

    this.carregaGraficoMovimentacoes();
    this.busyMovimentacoes = false;
  }

  private movimentacoesErro(error: any) {
    this.busyMovimentacoes = false;
  }

  carregaCards(cards: DashboardCardResponse) {
    this.cards = [
      {
        // title: 'Validação',
        title: 'Aprovação',
        icon: 'pi-eye',
        class: 'bg-primary2',
        valor: cards.validation,
        filters: {
          status: 'Aprovação'
        }
      },
      {
        title: 'Produção',
        icon: 'pi-cog',
        class: 'bg-primary',
        valor: cards.production,
        filters: {
          status: 'Produção'
        }
      },
      {
        title: 'Atrasados',
        icon: 'pi-info-circle',
        class: 'bg-secondary2',
        valor: cards.delay,
        filters: {
          status: 'Atrasados'
        }
      },
      {
        title: 'Aprovados/mês',
        icon: 'pi-thumbs-up',
        class: 'bg-secondary3',
        valor: cards.approved,
        filters: {
          status: 'Aprovados',
          // startDate: moment().startOf('month').format('YYYY-MM-DD 00:00:00'),  
          // endDate: moment().endOf('month').format('YYYY-MM-DD 23:59:59'),  
        }
      },
      {
        title: 'Entregues/mês',
        icon: 'pi-car',
        class: 'bg-primary3',
        valor: cards.delivered,
        filters: {
          status: 'Entregues',
          startDate: moment().startOf('month').format('YYYY-MM-DD 00:00:00'),  
          endDate: moment().endOf('month').format('YYYY-MM-DD 23:59:59'),  
        }
      },
      {
        title: 'Cancelados/mês',
        icon: 'pi-times-circle',
        class: 'bg-secondary',
        valor: cards.canceled,
        filters: {
          status: 'Cancelados',
          // startDate: moment().startOf('month').format('YYYY-MM-DD 00:00:00'),  
          // endDate: moment().endOf('month').format('YYYY-MM-DD 23:59:59'),  
        }
      },
    ];
  }

  private newLegendClickHandler = function (e: any, legendItem: any, legend: any) {
    // const index = legendItem.datasetIndex;
    // const ci = legend.chart;
    // if (ci.isDatasetVisible(index)) {
    //   ci.hide(index);
    //   legendItem.hidden = true;
    // } else {
    //   ci.show(index);
    //   legendItem.hidden = false;
    // }
  };

  carregaGraficoLocalizacoes() {
    const documentStyle = getComputedStyle(document.documentElement);
    const textColor = documentStyle.getPropertyValue('--text-color');
    const textColorSecondary = documentStyle.getPropertyValue('--text-color-secondary');
    const surfaceBorder = documentStyle.getPropertyValue('--surface-border');

    this.basicData = {
      labels: this.lblLocalizacoes,
      datasets: [
        {
          label: 'Pedidos',
          data: this.vlrLocalizacoes,
          backgroundColor: ['rgba(255, 159, 64, 0.2)', 'rgba(75, 192, 192, 0.2)', 'rgba(54, 162, 235, 0.2)', 'rgba(153, 102, 255, 0.2)'],
          borderColor: ['rgb(255, 159, 64)', 'rgb(75, 192, 192)', 'rgb(54, 162, 235)', 'rgb(153, 102, 255)'],
          borderWidth: 1
        }
      ]
    };

    this.basicOptions = {
      responsive: true,
      plugins: {
        legend: {
          // display: true,
          // position: 'top',
          // align: 'center',
          labels: {
            color: textColor,
            // boxWidth: 13,
            // textAlign: 'left',
            // padding: 3,
          },
          title: {
            display: true,
            color: textColor,
            text: 'Status da produção',
            font: {
              size: 13,
              weight: 'bold'
            }
          },
          onClick: this.newLegendClickHandler
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          ticks: {
            color: textColorSecondary
          },
          grid: {
            color: surfaceBorder,
            drawBorder: false
          }
        },
        x: {
          ticks: {
            color: textColorSecondary
          },
          grid: {
            color: surfaceBorder,
            drawBorder: false
          }
        }
      }
    };
  }

  carregaGraficoMovimentacoes() {
    const documentStyle = getComputedStyle(document.documentElement);
    const textColor = documentStyle.getPropertyValue('--text-color');
    const textColorSecondary = documentStyle.getPropertyValue('--text-color-secondary');
    const surfaceBorder = documentStyle.getPropertyValue('--surface-border');

    this.data = {
      labels: this.lblMovimentacoes,
      datasets: [
        {
          // label: 'Validação',
          label: 'Produção',
          data: this.vlrMovimentacoesValidacao,
          fill: false,
          borderColor: documentStyle.getPropertyValue('--red-500'),
          // tension: 0.4
        },
        {
          label: 'Em produção',
          data: this.vlrMovimentacoesProducao,
          fill: false,
          borderColor: documentStyle.getPropertyValue('--pink-500'),
          // tension: 0.4
        },
        {
          label: 'Aprovados',
          data: this.vlrMovimentacoesAprovados,
          fill: false,
          borderColor: documentStyle.getPropertyValue('--green-500'),
          // tension: 0.4
        },
        {
          label: 'Entregues',
          // data: [20, 38, 30, 11, 56, 37, 51],
          data: this.vlrMovimentacoesEntregues,
          fill: false,
          borderColor: documentStyle.getPropertyValue('--blue-500'),
          // tension: 0.4
        },
        {
          label: 'Cancelados',
          // data: [10, 28, 20, 9, 46, 17, 10],
          data: this.vlrMovimentacoesCancelados,
          fill: false,
          borderColor: documentStyle.getPropertyValue('--red-500'),
          // tension: 0.4
        },

      ]
    };

    this.options = {
      responsive: true,
      maintainAspectRatio: false,
      aspectRatio: 0.6,
      plugins: {
        legend: {
          // display: true,
          // position: 'top',
          // align: 'center',
          labels: {
            color: textColor,
            // boxWidth: 13,
            // textAlign: 'left',
            // padding: 3,
          },
          title: {
            display: true,
            color: textColor,
            text: 'Evolução dos pedidos',
            font: {
              size: 13,
              weight: 'bold'
            }
          },
          onClick: this.newLegendClickHandler
        }
      },
      scales: {
        x: {
          ticks: {
            color: textColorSecondary
          },
          grid: {
            color: surfaceBorder,
            drawBorder: false
          }
        },
        y: {
          ticks: {
            color: textColorSecondary
          },
          grid: {
            color: surfaceBorder,
            drawBorder: false
          }
        }
      }
    };
  }
}